import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Workflow,
  RefreshCw,
  Plug,
  Circle,
  Copy,
  CheckCircle,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useAdConnections, AdConnectionSummary } from '../../hooks/useAdConnections';
import { useCrmConnection } from '../../hooks/useCrmConnection';
import { useLeadCaptureOnboarding } from '../../hooks/useLeadCaptureOnboarding';
import { ConnectMetaModal } from '../../components/ads/ConnectMetaModal';
import { ConnectGoogleAdsModal } from '../../components/ads/ConnectGoogleAdsModal';
import { ConnectMyCrmModal } from '../../components/crm/ConnectMyCrmModal';
import { ConnectMondayCard } from '../../components/crm/ConnectMondayCard';
import { collection, getDocs } from 'firebase/firestore';
import { db as firestore } from '../../lib/firebase';
import { buildLeadIngestUrl } from './leadIngestUrl';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
  offices?: Array<{ officeId: string; officeName: string }>;
};

type IntegrationStatus = 'connected' | 'not_connected' | 'invite_only';

interface IntegrationCardDef {
  id: string;
  name: string;
  brandColor: string;
  iconText: string;
  authType: string;
  description: string;
  scopes?: string;
}

const CARDS: IntegrationCardDef[] = [
  {
    id: 'google_ads',
    name: 'Google Ads',
    brandColor: '#4285f4',
    iconText: 'G',
    authType: 'OAuth (Google account sign-in)',
    description:
      'Pull lead form submissions, campaign performance, ad spend, and keyword data. Calculate cost per settled customer.',
    scopes: 'adwords.readonly',
  },
  {
    id: 'meta_ads',
    name: 'Meta (Facebook & Instagram)',
    brandColor: '#1877f2',
    iconText: 'f',
    authType: 'OAuth (Facebook account)',
    description:
      'Pull lead ad submissions, campaign performance, ad spend, and audience insights across Facebook and Instagram.',
    scopes: 'ads_read, leads_retrieval',
  },
  {
    id: 'mycrm',
    name: 'MyCRM (Loan Market)',
    brandColor: '#e17055',
    iconText: 'MC',
    authType: 'Credentials (API key)',
    description:
      'Push leads into MyCRM for broker processing. Pull settled deals for trail leakage detection and pipeline analytics.',
  },
  {
    id: 'resend',
    name: 'Resend (Email)',
    brandColor: '#000000',
    iconText: 'R',
    authType: 'API Key',
    description:
      'Send transactional and marketing emails. Open/click tracking. Webhook delivery events. $20 USD/mo for 50K emails.',
  },
  // Monday.com (Property) — rendered by <ConnectMondayCard /> below the
  // generic grid so it can drive its own OAuth flow + board picker.
  {
    id: 'smokeball',
    name: 'Smokeball (Legal)',
    brandColor: '#00b894',
    iconText: 'S',
    authType: 'API Key (requires invitation)',
    description:
      'Pull legal case status from Smokeball. Track conveyancing progress alongside settlements. API access requires invitation.',
  },
];

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const StatusBadge: React.FC<{ status: IntegrationStatus }> = ({ status }) => {
  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <Circle className="w-2 h-2 fill-emerald-400" />
        Connected
      </span>
    );
  }
  if (status === 'invite_only') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
        Invite only
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-500/15 text-gray-300 border border-gray-500/30">
      Not connected
    </span>
  );
};

interface IntegrationCardProps {
  def: IntegrationCardDef;
  status: IntegrationStatus;
  accountLabel?: string;
  lastSync?: string | null;
  lastSyncResult?: string | null;
  activityText?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onSyncNow?: () => void;
  disabled?: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  def,
  status,
  accountLabel,
  lastSync,
  lastSyncResult,
  activityText,
  onConnect,
  onDisconnect,
  onSyncNow,
  disabled,
}) => {
  const isConnected = status === 'connected';
  return (
    <div
      className={`rounded-lg border ${
        isConnected
          ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
          : 'border-gray-700 bg-gray-800/40'
      } p-5`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded flex items-center justify-center font-bold text-white flex-shrink-0"
          style={{ background: def.brandColor }}
        >
          {def.iconText}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-white">{def.name}</h3>
            <StatusBadge status={status} />
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">{def.authType}</p>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">
        {def.description}
      </p>

      {isConnected && (
        <div className="rounded-md border border-gray-700/50 bg-gray-900/40 px-3 py-2 space-y-0.5 mb-4 text-[11px]">
          {accountLabel && (
            <div className="text-gray-400">
              <span className="text-gray-500">Account:</span>{' '}
              <span className="font-mono text-gray-300">{accountLabel}</span>
            </div>
          )}
          <div className="text-gray-400">
            <span className="text-gray-500">Last sync:</span>{' '}
            <span className="text-gray-300">{formatDate(lastSync ?? null)}</span>
            {lastSyncResult && (
              <span className="text-gray-500"> · {lastSyncResult}</span>
            )}
          </div>
          {activityText && (
            <div className="text-gray-400">
              <span className="text-gray-500">Activity:</span>{' '}
              <span className="text-gray-300">{activityText}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {isConnected ? (
          <>
            {onSyncNow && (
              <button
                type="button"
                onClick={onSyncNow}
                disabled={disabled}
                className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <RefreshCw className="w-3 h-3" />
                Sync now
              </button>
            )}
            {onDisconnect && (
              <button
                type="button"
                onClick={onDisconnect}
                disabled={disabled}
                className="px-3 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 text-xs disabled:opacity-50"
              >
                Disconnect
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={onConnect}
            disabled={disabled || status === 'invite_only'}
            className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plug className="w-3 h-3" />
            {status === 'invite_only' ? 'Request access' : `Connect ${def.name.split(' ')[0]}`}
          </button>
        )}
      </div>
    </div>
  );
};

const LeadCaptureIntegrationsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const companyId = ctx.userData?.companyId;
  // Marketing gets list + connect for ad accounts only. Credential surfaces
  // (MyCRM, Monday.com) and admin-only actions (disconnect, rotate webhook
  // token) are hidden (marketing RBAC §1 / §5).
  const isMarketing = ctx.userData?.role === 'marketing';
  const offices = useMemo(() => ctx.offices ?? [], [ctx.offices]);

  const {
    connections: adConnections,
    loading: adLoading,
    error: adError,
    refresh: refreshAds,
    disconnect: disconnectAd,
  } = useAdConnections(companyId);
  const {
    connections: crmConnections,
    loading: crmLoading,
    error: crmError,
    refresh: refreshCrm,
    triggerSync,
  } = useCrmConnection(companyId);
  const { generatePerOfficeToken, getGoLiveChecklist } =
    useLeadCaptureOnboarding(companyId);

  // Real Resend state. The card was hardcoded to 'not_connected', so a tenant
  // that HAD saved a key and verified its domain still read "Not connected",
  // and the Connect button was enabled with an undefined handler (a dead
  // click). getGoLiveChecklist already reports both facts and is the same
  // admin-gated callable the wizard uses, so no new read surface is needed.
  const [resendState, setResendState] = useState<{
    configured: boolean;
    verified: boolean;
    detail: string | null;
  } | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getGoLiveChecklist()
      .then((r) => {
        if (cancelled) return;
        const keyItem = r.checklist.find((c) => c.key === 'resend_api_key');
        const domainItem = r.checklist.find((c) => c.key === 'domain_verified');
        // Marketing has those two items redacted out of the checklist; leave
        // the state null so the card shows "unknown" rather than a false
        // "not connected".
        if (!keyItem && !domainItem) return;
        setResendState({
          configured: Boolean(keyItem?.completed),
          verified: Boolean(domainItem?.completed),
          detail: domainItem?.detail ?? null,
        });
      })
      .catch((err) => {
        console.error('[Integrations] resend state fetch failed', err);
        if (!cancelled) {
          setResendError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, getGoLiveChecklist]);

  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [myCrmModalOpen, setMyCrmModalOpen] = useState(false);

  // Per-office webhook tokens for the routing table. Loaded once from
  // Firestore; rotating a token re-writes the doc + refreshes.
  const [officeTokens, setOfficeTokens] = useState<
    Record<string, { token: string; rotatedAt: string | null; disabled: boolean } | null>
  >({});
  // Stored shape is `rotations[].match.officeId`, NOT a top-level officeId.
  // The page read the legacy flat shape, so the lookup below never matched
  // and every office reported "No brokers configured" no matter how the
  // rotation was set up.
  const [routingRules, setRoutingRules] = useState<Array<{
    match?: { officeId?: string | null };
    officeId?: string;
    brokerIds: string[];
  }>>([]);
  // A failed read of the per-office tokens / rotations used to be logged and
  // nothing else, so the table rendered as "no tokens, no brokers" -- which
  // reads as a configuration problem rather than a fetch failure.
  const [routingError, setRoutingError] = useState<string | null>(null);
  const [tokenReloadKey, setTokenReloadKey] = useState(0);
  const [rotating, setRotating] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [webhookSnap, routingSnap] = await Promise.all([
          getDocs(collection(firestore, `companies/${companyId}/lead_webhook_config`)),
          getDocs(collection(firestore, `companies/${companyId}/lead_routing_rules`)),
        ]);
        if (cancelled) return;
        const next: typeof officeTokens = {};
        webhookSnap.docs.forEach((doc) => {
          if (doc.id === 'default') return;
          if (doc.id.startsWith('source_')) return;
          const d = doc.data() as {
            token?: string;
            rotatedAt?: { toDate?: () => Date };
            disabled?: boolean;
          };
          next[doc.id] = {
            token: d.token ?? '',
            rotatedAt: d.rotatedAt?.toDate
              ? d.rotatedAt.toDate().toISOString()
              : null,
            disabled: !!d.disabled,
          };
        });
        setOfficeTokens(next);
        const defaultDoc = routingSnap.docs.find((d) => d.id === 'default');
        if (defaultDoc) {
          const d = defaultDoc.data() as {
            rotations?: Array<{
              match?: { officeId?: string | null };
              officeId?: string;
              brokerIds: string[];
            }>;
          };
          setRoutingRules(Array.isArray(d.rotations) ? d.rotations : []);
        }
      } catch (err) {
        console.error('[Integrations] token/routing fetch failed', err);
        if (!cancelled) {
          setRoutingError(
            err instanceof Error ? err.message : String(err)
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, tokenReloadKey]);

  const handleRotateOfficeToken = async (officeId: string, officeName: string) => {
    // Rotating invalidates the URL every website form for this office is
    // posting to, so leads stop arriving until each form is updated. That is
    // not a one-click action.
    if (
      !window.confirm(
        `Rotate the webhook token for ${officeName}?\n\nThe current URL stops accepting leads once the grace period ends. Every website form posting to it must be updated with the new URL.`
      )
    ) {
      return;
    }
    setRotating((prev) => ({ ...prev, [officeId]: true }));
    setRoutingError(null);
    try {
      await generatePerOfficeToken(officeId);
      setTokenReloadKey((k) => k + 1);
    } catch (err) {
      console.error('[Integrations] token rotate failed', err);
      setRoutingError(err instanceof Error ? err.message : String(err));
    } finally {
      setRotating((prev) => ({ ...prev, [officeId]: false }));
    }
  };

  // Confirm before disconnecting an ad account: it stops spend ingestion and
  // the attribution reports that depend on it.
  const handleDisconnectAd = (connectionId: string, name: string) => {
    if (
      !window.confirm(
        `Disconnect ${name}?\n\nAd spend will stop syncing and attribution reporting for this account will stop updating. You can reconnect later.`
      )
    ) {
      return;
    }
    void disconnectAd(connectionId);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  const refresh = () => {
    refreshAds();
    refreshCrm();
    setTokenReloadKey((k) => k + 1);
  };

  // Derive status per card from the two hook sources.
  const findAd = (
    provider: 'meta_ads' | 'google_ads'
  ): AdConnectionSummary | undefined =>
    adConnections.find((c) => c.provider === provider && c.status === 'active');
  const mycrmActive = crmConnections.find((c) => c.status === 'active');

  const cardsWithState = CARDS.map((def) => {
    if (def.id === 'google_ads') {
      const conn = findAd('google_ads');
      return {
        def,
        status: (conn ? 'connected' : 'not_connected') as IntegrationStatus,
        accountLabel: conn?.adAccountId,
        lastSync: conn?.lastSyncAt ?? null,
        lastSyncResult: conn?.lastSyncResult,
        activityText: undefined,
        onConnect: () => setGoogleModalOpen(true),
        onDisconnect: conn
          ? () => handleDisconnectAd(conn.connectionId, def.name)
          : undefined,
        onSyncNow: undefined,
      };
    }
    if (def.id === 'meta_ads') {
      const conn = findAd('meta_ads');
      return {
        def,
        status: (conn ? 'connected' : 'not_connected') as IntegrationStatus,
        accountLabel: conn?.adAccountId,
        lastSync: conn?.lastSyncAt ?? null,
        lastSyncResult: conn?.lastSyncResult,
        activityText: undefined,
        onConnect: () => setMetaModalOpen(true),
        onDisconnect: conn
          ? () => handleDisconnectAd(conn.connectionId, def.name)
          : undefined,
        onSyncNow: undefined,
      };
    }
    if (def.id === 'mycrm') {
      return {
        def,
        status: (mycrmActive ? 'connected' : 'not_connected') as IntegrationStatus,
        accountLabel: mycrmActive
          ? `Connection ${mycrmActive.connectionId.slice(0, 10)}…`
          : undefined,
        lastSync: mycrmActive?.lastSyncAt ?? null,
        lastSyncResult: mycrmActive?.lastSyncResult,
        activityText: mycrmActive
          ? `${mycrmActive.dealsSynced ?? 0} deals · ${mycrmActive.advisersSynced ?? 0} advisers`
          : undefined,
        onConnect: () => setMyCrmModalOpen(true),
        onDisconnect: undefined,
        onSyncNow: mycrmActive
          ? () => void triggerSync(mycrmActive.connectionId)
          : undefined,
      };
    }
    if (def.id === 'resend') {
      // Resend is configured in Setup Wizard step 2, so there is no
      // connection doc; the state comes from the go-live checklist instead.
      // 'Connect' navigates to that wizard step rather than being an enabled
      // button with no handler.
      const configured = resendState?.configured ?? false;
      const verified = resendState?.verified ?? false;
      return {
        def,
        // Fully connected only when the key is saved AND the domain is
        // verified: an unverified domain cannot send.
        status: (configured && verified
          ? 'connected'
          : 'not_connected') as IntegrationStatus,
        accountLabel: configured
          ? verified
            ? 'API key saved, domain verified'
            : 'API key saved, domain not verified'
          : undefined,
        lastSync: null,
        lastSyncResult: null,
        activityText: resendState?.detail ?? undefined,
        onConnect: () => navigate('/leads/setup'),
        onDisconnect: undefined,
        onSyncNow: undefined,
      };
    }
    if (def.id === 'smokeball') {
      return {
        def,
        status: 'invite_only' as IntegrationStatus,
        accountLabel: undefined,
        lastSync: null,
        lastSyncResult: null,
        activityText: undefined,
        onConnect: undefined,
        onDisconnect: undefined,
        onSyncNow: undefined,
      };
    }
    return {
      def,
      status: 'not_connected' as IntegrationStatus,
      accountLabel: undefined,
      lastSync: null,
      lastSyncResult: null,
      activityText: undefined,
      onConnect: undefined,
      onDisconnect: undefined,
      onSyncNow: undefined,
    };
  })
    // Marketing can't operate the MyCRM credential surface, so hide that card.
    .filter((c) => !(isMarketing && c.def.id === 'mycrm'))
    // Disconnect is admin-only; strip the action for marketing so they keep
    // list + connect only.
    .map((c) => (isMarketing ? { ...c, onDisconnect: undefined } : c));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Workflow}
        title="Integrations"
        subtitle="Connect your ad accounts, CRM, and email provider — OAuth sign-in, no API keys to copy"
        onRefresh={refresh}
        isRefreshing={adLoading || crmLoading}
      />

      {/* These were previously swallowed: a failed fetch left every card
          reading "Not connected", which is indistinguishable from a tenant
          that genuinely has nothing connected. */}
      {(adError || crmError || resendError || routingError) && (
        <div
          role="alert"
          className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 space-y-1"
        >
          <div className="font-medium">
            Some integration details could not be loaded.
          </div>
          {adError && <div className="text-xs">Ad accounts: {adError}</div>}
          {crmError && <div className="text-xs">CRM: {crmError}</div>}
          {resendError && <div className="text-xs">Email provider: {resendError}</div>}
          {routingError && <div className="text-xs">Per-office routing: {routingError}</div>}
          <div className="text-xs text-red-200/70">
            Connection states shown below may be incomplete. Try refreshing.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cardsWithState.map((c) => (
          <IntegrationCard key={c.def.id} {...c} />
        ))}
        {/* Monday.com — full-featured card with OAuth + board picker. Renders
            inline in the same grid so it auto-promotes from "available" to
            "active" via the live Firestore subscription on crm_connections.
            Hidden for marketing (credential surface, marketing RBAC §5). */}
        {!isMarketing && <ConnectMondayCard companyId={companyId} />}
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
        <h3 className="text-sm font-semibold text-white mb-2">
          Per-office lead routing
        </h3>
        <p className="text-xs text-gray-400 mb-3 max-w-2xl">
          Each office gets its own webhook URL and broker rotation. A website
          form posts to the URL for its office, and leads are assigned
          round-robin to that office&rsquo;s brokers. Configure the rotation
          order in the Setup Wizard Step 8.
        </p>
        {offices.length === 0 ? (
          <div className="rounded-md border border-gray-700/50 bg-gray-900/40 p-4 text-center text-xs text-gray-500">
            No offices defined on this company yet. Create them in Settings
            first.
          </div>
        ) : (
          <div className="rounded-md border border-gray-700 bg-gray-900/60 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-800 text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-2">Office</th>
                  <th className="px-4 py-2">Webhook URL</th>
                  <th className="px-4 py-2">Broker rotation</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800 text-gray-300">
                {offices.map((office) => {
                  const tok = officeTokens[office.officeId];
                  // Company id included: without `c` leadIngest 400s.
                  const url = tok && companyId
                    ? buildLeadIngestUrl({
                        companyId,
                        token: tok.token,
                        officeId: office.officeId,
                      })
                    : null;
                  // Prefer the current `match.officeId`; fall back to the
                  // legacy flat field so an un-migrated doc still resolves.
                  const rotation = routingRules.find(
                    (r) =>
                      (r.match?.officeId ?? r.officeId) === office.officeId
                  );
                  return (
                    <tr key={office.officeId} className="hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-white font-medium">
                        {office.officeName}
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px]">
                        {url ? (
                          <div className="flex items-center gap-1.5 max-w-md">
                            <code className="truncate text-gray-400">
                              {url}
                            </code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(url)}
                              className="p-1 rounded hover:bg-gray-700 text-gray-400 flex-shrink-0"
                              title="Copy"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-500">
                            Token not yet issued
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {rotation && rotation.brokerIds.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[11px]">
                            <CheckCircle className="w-3 h-3 text-emerald-400" />
                            {rotation.brokerIds.length} broker
                            {rotation.brokerIds.length > 1 ? 's' : ''} in
                            rotation
                          </span>
                        ) : (
                          <span className="text-gray-500 text-[11px]">
                            No brokers configured
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {/* Rotate / generate webhook token is admin-only
                            (rotateWebhookToken); hidden for marketing, who
                            keep view + copy of the URL (marketing RBAC §5). */}
                        {!isMarketing && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRotateOfficeToken(
                                office.officeId,
                                office.officeName
                              )
                            }
                            disabled={rotating[office.officeId]}
                            className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs text-white disabled:opacity-50"
                          >
                            {rotating[office.officeId]
                              ? 'Rotating…'
                              : url
                                ? 'Rotate'
                                : 'Generate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConnectMetaModal
        companyId={companyId}
        open={metaModalOpen}
        onClose={() => setMetaModalOpen(false)}
        onConnected={() => refreshAds()}
      />
      <ConnectGoogleAdsModal
        companyId={companyId}
        open={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
        onConnected={() => refreshAds()}
      />
      <ConnectMyCrmModal
        companyId={companyId}
        open={myCrmModalOpen}
        onClose={() => setMyCrmModalOpen(false)}
        onConnected={() => refreshCrm()}
      />
    </div>
  );
};

export default LeadCaptureIntegrationsPage;
